'use client';

import { Calendar } from '@workspace/ui/components/calendar';
import { Input } from '@workspace/ui/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { cn } from '@workspace/ui/lib';
import { format } from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';
import { z } from 'zod';
// import { useTranslations } from 'next-intl';

export const dateRangeFieldSchema = (errorMessage: string) =>
  z
    .object({
      from: z.date(),
      to: z.date(),
    })
    .optional()
    .superRefine((data, ctx) => {
      if (!data) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: errorMessage,
        });
      }
    });

export function DateRangePicker({
  value,
  onChange,
  error,
  onBlur,
  clearable = true,
  placeholder,
  disabled = false,
  readOnly = false,
  size = 'base',
  displayFormat = 'dd/MM/yyyy',
  formatValue,
  disabledDates,
}: {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  error?: boolean;
  onBlur?: () => void;
  clearable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  size?: 'base' | 'sm';
  /** date-fns format applied to each date in the range (default: `dd/MM/yyyy`). */
  displayFormat?: string;
  /** Custom formatter for the full range label; overrides `displayFormat` when provided. */
  formatValue?: (range: DateRange) => string;
  disabledDates?: (date: Date) => boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [selectedRange, setSelectedRange] = React.useState<DateRange | undefined>(value);
  // const t = useTranslations('global');
  React.useEffect(() => {
    setSelectedRange(value);
  }, [value]);

  const formatDateRange = (range: DateRange | undefined) => {
    if (!range?.from) return '';
    if (formatValue) return formatValue(range);
    if (!range.to) return format(range.from, displayFormat);
    return `${format(range.from, displayFormat)} - ${format(range.to, displayFormat)}`;
  };

  const handleClear = (e: React.MouseEvent) => {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    setSelectedRange(undefined);
    onChange(undefined);
  };

  return (
    <Popover
      open={open}
      onOpenChange={
        readOnly
          ? undefined
          : (isOpen) => {
              if (!isOpen) onBlur?.();
              if (disabled && isOpen) return;
              setOpen(isOpen);
            }
      }
    >
      <PopoverTrigger asChild disabled={disabled || readOnly}>
        <div
          className={cn(
            'relative w-full outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-0',
            size === 'sm' ? 'rounded-xl' : 'rounded-2xl'
          )}
        >
          <Input
            size={size}
            className={
              readOnly
                ? 'border-grey-300 text-grey-600! dark:border-grey-600 !cursor-default'
                : 'cursor-pointer caret-transparent'
            }
            placeholder={placeholder || 'Select a date range'}
            value={formatDateRange(selectedRange)}
            capitalizeFirstLetter={false}
            endIcon={
              disabled || readOnly ? (
                <CalendarIcon
                  className={cn(disabled ? 'text-grey-300! dark:text-grey-900!' : 'text-grey-600!')}
                />
              ) : selectedRange?.from && clearable ? (
                <X
                  className={cn('text-primary cursor-pointer dark:text-neutral-100')}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={handleClear}
                />
              ) : (
                <CalendarIcon className={cn('text-primary cursor-pointer dark:text-neutral-100')} />
              )
            }
            readOnly={readOnly}
            onChange={() => {}}
            aria-invalid={error}
            disabled={disabled}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] max-w-[440px] min-w-[300px] p-0 sm:min-w-[356px]">
        <Calendar
          mode="range"
          selected={selectedRange}
          disabled={disabledDates}
          onSelect={(range) => {
            if (range && 'from' in range) {
              const newRange = {
                from: range.from,
                to: range.to || range.from,
              };
              setSelectedRange(newRange);
              onChange(newRange);
            }
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
